import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useGlobalSearch } from '../../context/GlobalSearchContext';
import { useLabContext } from '../../context/LabContext';
import { useNavigate } from 'react-router-dom';
import {
  Search, Bell, User, LogOut, Settings, Moon, Sun, HelpCircle,
  ChevronDown, Mail, Clock, Building2, Sparkles, Phone,
  FlaskConical, AlertTriangle, DollarSign,
  Calendar, FileText, Package, ScanLine
} from 'lucide-react';
import BatchSearchModal from './BatchSearchModal';
import IdentitySwitcher from '../common/IdentitySwitcher';
import api from '../../services/api';
import { useTheme } from '../../context/ThemeContext';
import { useHelp } from '../help/HelpContext';
import CallLogWizard from '../lis/CallLogWizard';
import './Header.css';

import { formatLabDate } from '../../utils/labTime';
// Fallback used until /api/version responds (or if it fails). The live
// value comes from the app_releases DB table — see app/routes/version.py.
const FALLBACK_VERSION = '10.6.180';
const FALLBACK_HISTORY = [{
  version: '8.6.0-rc1',
  date: 'April 25, 2026',
  sections: [{
    title: 'Loading…',
    items: ['Live changelog comes from /api/version/history — refresh in a moment if it stays empty.'],
  }],
}];

const Header = ({ currentUser, onLogout }) => {
  // Which company this session belongs to. Asked once on mount rather than
  // when the dropdown opens, so the company is already there when it does —
  // and asked at all because a client login cannot call the companies
  // endpoint, leaving `selectedCompany` null and the dropdown showing a
  // laboratory with nothing above it.
  const [identityContext, setIdentityContext] = React.useState(null);
  // Company, laboratories and client practices as the SERVER sees this
  // session. The header's own sources are administrator endpoints that refuse
  // a client login, so it fell back to a stale company id in localStorage —
  // which is how a Xpiva session came to display "California", a laboratory
  // belonging to 9 Labs.
  const [myContext, setMyContext] = React.useState(null);
  const [selectedPractice, setSelectedPractice] = React.useState(() => {
    try { return localStorage.getItem('selectedClientId') || ''; } catch { return ''; }
  });
  const choosePractice = (id) => {
    try {
      if (id) localStorage.setItem('selectedClientId', id);
      else localStorage.removeItem('selectedClientId');
    } catch { /* storage blocked */ }
    setSelectedPractice(id || '');
    // A hard reload: every list on screen was fetched for the old practice.
    window.location.reload();
  };
  React.useEffect(() => {
    const tok = (() => { try {
      return sessionStorage.getItem('rep_token')
          || sessionStorage.getItem('portal_token')
          || localStorage.getItem('token');
    } catch { return null; } })();
    if (!tok) return;
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/identity/me`,
          { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.current) setIdentityContext(d.current); })
      .catch(() => { /* the company label is a nicety, never worth failing */ });
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/identity/my-context`,
          { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setMyContext(d); })
      .catch(() => { /* same */ });
  }, []);
  // A patient signed in on the staff shell has no use for the laboratory
  // tools in this bar, and no business in them. Hidden here; the endpoints
  // behind them are gated with require_staff, because hiding a button is
  // presentation, not access control.
  // Roles that are NOT laboratory staff, even though they sign in on the
  // staff shell. A `role='client'` account is an external ordering practice —
  // a customer — and a `role='patient'` is a legacy portal login.
  //
  // What is hidden below is presentation, not a security boundary, and it is
  // worth being exact about which: the endpoints behind these icons are scoped
  // per USER, not per role. `/communications/inbox` reads
  // `user_email_settings WHERE user_id = current_user.id`, and the
  // notification feed is the caller's own. Checked against a client token:
  // both answer 200 with nothing in them.
  //
  // So no data was reachable that should not have been. They are hidden
  // because a customer has no use for the laboratory's own tooling and every
  // one of these opens something empty or irrelevant for them.
  const isExternalUser = ['patient', 'client']
    .includes((currentUser?.role || '').toLowerCase());

    const { searchTerm, setSearchTerm } = useGlobalSearch();
  const { parentCompanies, labs, selectedCompanyId, selectedLabId, selectedCompany, selectedLab, isCorporateUser, selectCompany, selectLab, clearLab } = useLabContext();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [showCallWizard, setShowCallWizard] = useState(false);
  // Live version data — fetched on mount from /api/version. Falls back
  // to the FALLBACK_* constants if the API is unreachable, so the menu
  // never breaks even when the backend is down.
  const [appVersion, setAppVersion] = useState(FALLBACK_VERSION);
  const [versionHistory, setVersionHistory] = useState(FALLBACK_HISTORY);
  const [showLabSelector, setShowLabSelector] = useState(false);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showGmail, setShowGmail] = useState(false);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const { dark: darkMode, toggleTheme, tokens } = useTheme();
  const { openSidebar: openHelpSidebar } = useHelp();
  const [gmailEmails, setGmailEmails] = useState([]);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [gmailError, setGmailError] = useState('');
  // Tenant-level choice of inbox backend — 'imap' (per-user creds) or
  // 'gmail_api' (DWD service-account impersonation). Set in Corporate
  // Billing → Edit Company → Email integration provider. Defaults to
  // 'imap' if the company doesn't have the field set yet, or if the
  // fetch fails (so the bell still works under the old behavior).
  const [emailProvider, setEmailProvider] = useState('imap');

  const userMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const labSelectorRef = useRef(null);
  const gmailRef = useRef(null);
  const navigate = useNavigate();


  // Notification type -> icon mapping
  const NOTIF_ICONS = {
    result: FlaskConical,
    critical: AlertTriangle,
    payment: DollarSign,
    appointment: Calendar,
    claim: FileText,
    inventory: Package,
    system: Bell,
  };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/api/notifications/unread-count');
      setUnreadCount(res.data?.unread_count || 0);
    } catch (err) {
      // silently ignore - notifications are non-critical
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/api/notifications?limit=10&include_read=true');
      setNotifications(res.data || []);
    } catch (err) {
      // silently ignore
    }
  }, []);

  // Fetch unread count on mount and every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full notifications when dropdown is opened
  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
  }, [showNotifications, fetchNotifications]);

  // Resolve which inbox backend to use for the bell — tenant-level setting
  // saved in parent_companies.integration_settings.email_provider. Fetched
  // once per company switch; on failure we stay on the safe IMAP default.
  useEffect(() => {
    if (!selectedCompanyId) return;
    let cancelled = false;
    api.get(`/api/v1/saas/companies/${selectedCompanyId}/email-provider`)
      .then(r => { if (!cancelled) setEmailProvider(r.data?.email_provider || 'imap'); })
      .catch(() => { if (!cancelled) setEmailProvider('imap'); });
    return () => { cancelled = true; };
  }, [selectedCompanyId]);

  // Fetch live version + changelog on mount. The API returns
  // {version, released_at, changelog:[{title, items}]}. We normalise
  // released_at to a human-readable date so the existing render stays.
  useEffect(() => {
    let cancelled = false;
    const formatDate = (iso) => {
      if (!iso) return '';
      try { return formatLabDate(iso); }
      catch { return ''; }
    };
    const normalise = (row) => ({
      version: row.version || 'unknown',
      date: formatDate(row.released_at),
      sections: Array.isArray(row.changelog) ? row.changelog : [],
    });
    Promise.all([
      api.get('/api/version'),
      api.get('/api/version/history?limit=50'),
    ]).then(([cur, hist]) => {
      if (cancelled) return;
      if (cur.data?.version) setAppVersion(cur.data.version);
      if (Array.isArray(hist.data) && hist.data.length) {
        setVersionHistory(hist.data.map(normalise));
      }
    }).catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);

  const handleMarkRead = async (notifId) => {
    try {
      await api.patch(`/api/notifications/${notifId}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      // silently ignore
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch('/api/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      // silently ignore
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (labSelectorRef.current && !labSelectorRef.current.contains(event.target)) {
        setShowLabSelector(false);
      }
      if (gmailRef.current && !gmailRef.current.contains(event.target)) {
        setShowGmail(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLabSelect = (lab) => {
    selectLab(lab.lab_id);
    setShowLabSelector(false);
  };

  const handleCompanySelect = (company) => {
    selectCompany(company.parent_company_id);
    setShowCompanySelector(false);
  };

  const currentPath = window.location.pathname;
  const searchConfig = {
    '/patients': { placeholder: 'Search patients by name, MRN, phone, email...', path: '/patients' },
    '/orders': { placeholder: 'Search orders by number, patient, status...', path: '/orders' },
    '/results': { placeholder: 'Search results by patient, test, status...', path: '/results' },
    '/requisitions': { placeholder: 'Search requisitions by number, patient...', path: '/requisitions' },
    '/specimens': { placeholder: 'Search specimens by ID, patient, status...', path: '/specimens' },
    '/clients': { placeholder: 'Search clients by name, NPI, account...', path: '/clients' },
    '/claims': { placeholder: 'Search claims by number, payer, patient...', path: '/claims' },
    '/providers': { placeholder: 'Search providers by name, NPI...', path: '/providers' },
    '/rcm/payers': { placeholder: 'Search payers by name, ID, clearinghouse...', path: '/rcm/payers', live: true },
    '/rcm/paper-claims': { placeholder: 'Search paper claims by number, payer, patient...', path: '/rcm/paper-claims', live: true },
    '/rcm/work-queues': { placeholder: 'Search tasks...', path: '/rcm/work-queues', live: true },
    '/billing/payers': { placeholder: 'Search payers by name, ID, clearinghouse...', path: '/billing/payers', live: true },
    '/dispatch-pickups': { placeholder: 'Search dispatch — patient, client, status, tech...', path: '/dispatch-pickups', live: true },
    '/dispatch': { placeholder: 'Search dispatch — patient, client, status, tech...', path: '/dispatch-pickups', live: true },
    '/interfaces': { placeholder: 'Search analyzers — name, manufacturer, model...', path: '/interfaces', live: true },
    '/chat': { placeholder: 'Search this channel — text, sender, tag...', path: '/chat', live: true },
    '/cms': { placeholder: 'Search pages by title or slug...', path: '/cms', live: true },
    '/settings': { placeholder: 'Search records...', path: '/settings' },
  };
  const matchedConfig = Object.entries(searchConfig).find(([prefix]) => currentPath.startsWith(prefix));

  // A mounted page can opt into live-filtering regardless of its route by
  // dispatching 'live-search-register' (with an optional placeholder) and
  // 'live-search-unregister' on unmount. This is how the Interfaces page
  // gets live search even when it renders INSIDE Settings (/settings), where
  // the URL never changes to /interfaces.
  const [externalLive, setExternalLive] = React.useState(null); // {placeholder} | null
  React.useEffect(() => {
    const onReg = (e) => setExternalLive({ placeholder: e?.detail?.placeholder || null });
    const onUnreg = () => setExternalLive(null);
    window.addEventListener('live-search-register', onReg);
    window.addEventListener('live-search-unregister', onUnreg);
    return () => {
      window.removeEventListener('live-search-register', onReg);
      window.removeEventListener('live-search-unregister', onUnreg);
    };
  }, []);

  const liveFilterPage = !!((matchedConfig && matchedConfig[1].live) || externalLive);
  const searchPlaceholder = (externalLive && externalLive.placeholder)
    || (matchedConfig ? matchedConfig[1].placeholder : 'Search patients, orders, results...');
  const searchTargetPath = matchedConfig ? matchedConfig[1].path : '/patients';

  // Broadcast every keystroke to pages that want to live-filter (Payers, Paper Claims, etc.)
  // Pages subscribe via:  window.addEventListener('global-search', e => setQ(e.detail))
  React.useEffect(() => {
    if (liveFilterPage) {
      window.dispatchEvent(new CustomEvent('global-search',
        { detail: { q: searchTerm, path: currentPath } }));
    }
  }, [searchTerm, liveFilterPage, currentPath]);

  // Smart-jump dictionary — type a keyword in the header search, hit enter,
  // and we route to the matching settings page (or other tool). Matches if the
  // search term contains any of the keywords as a whole token. Add new entries
  // here whenever a new settings page is added.
  const SMART_JUMPS = [
    { keys: ['lcd', 'lcd codes', 'medical necessity', 'mednec', 'icd cpt'], target: '/settings?section=lcd-codes', label: 'LCD Codes' },
    { keys: ['icd', 'icd-10', 'icd codes', 'diagnosis codes'],              target: '/settings?section=icd-codes', label: 'ICD-10 Codes' },
    { keys: ['cpt'],                                                         target: '/settings?section=tests',     label: 'Tests / CPT codes' },
    { keys: ['tests', 'test catalog'],                                       target: '/settings?section=tests',     label: 'Tests' },
    { keys: ['panels'],                                                      target: '/settings?section=panels',    label: 'Panels' },
    { keys: ['bundles'],                                                     target: '/settings?section=bundles',   label: 'Bundles' },
    { keys: ['analytes'],                                                    target: '/settings?section=analytes',  label: 'Analytes' },
    { keys: ['validation agents', 'validation', 'agents'],                   target: '/settings?section=lab-validation', label: 'Validation Agents' },
    { keys: ['fee schedule', 'fee schedules', 'pricing'],                    target: '/settings?section=fee-schedules', label: 'Fee Schedules' },
    { keys: ['payers', 'payer'],                                             target: '/billing/payers',             label: 'Payers' },
    { keys: ['clearinghouse', 'clearinghouses'],                             target: '/settings?section=clearinghouses', label: 'Clearinghouses' },
    { keys: ['users', 'user management', 'staff'],                           target: '/settings?section=users',     label: 'Users' },
    { keys: ['roles', 'permissions'],                                        target: '/settings?section=roles',     label: 'Roles' },
    { keys: ['printers', 'printer'],                                         target: '/settings?section=printers',  label: 'Printers' },
    { keys: ['equipment', 'analyzers'],                                      target: '/settings?section=equipment', label: 'Equipment' },
    { keys: ['sysmex', 'beckman', 'agilent', 'roche', 'abbott', 'siemens', 'clinitek', 'multistix', 'urinalysis', 'interface', 'interfaces', 'analyzer interface', 'instrument interface', 'lab interface'], target: '/interfaces', label: 'Analyzer Interfaces' },
    { keys: ['eob', 'remittance'],                                           target: '/rcm/eobs',                   label: 'EOBs' },
    { keys: ['claims', 'claim'],                                             target: '/claims',                     label: 'Claims' },
    { keys: ['settlements', 'settlement', 'negotiation'],                    target: '/rcm/settlements',            label: 'Settlements' },
    { keys: ['fax', 'faxes', 'inbox'],                                       target: '/fax-inbox',                  label: 'Fax Inbox' },
    { keys: ['appointments', 'schedule', 'calendar'],                        target: '/admin/schedule',             label: 'Schedule' },
    { keys: ['inventory', 'supplies'],                                       target: '/inventory',                  label: 'Inventory' },
    { keys: ['purchasing', 'purchase orders', 'po'],                         target: '/purchasing',                 label: 'Purchasing' },
  ];

  const findSmartJump = (term) => {
    const t = (term || '').trim().toLowerCase();
    if (!t) return null;
    return SMART_JUMPS.find(j => j.keys.some(k => k === t || t.includes(k)));
  };

  // Scan a pile of barcodes, then search for all of them. The box already
  // takes several terms separated by semicolons; a scanner cannot type that,
  // because it ends every barcode with Enter and Enter submits the box.
  const [batchOpen, setBatchOpen] = React.useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (liveFilterPage) return;   // live-filter pages don't need a navigate
    // Smart jump first — typing "lcd" anywhere routes to the LCD Codes page,
    // "tests" routes to the Tests catalog, etc. Falls through to the per-page
    // navigate if nothing matched.
    const jump = findSmartJump(searchTerm);
    if (jump) {
      navigate(jump.target);
      return;
    }
    if (searchTerm.trim() && !currentPath.startsWith('/settings')) {
      navigate(searchTargetPath);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <header className="app-header">
      <div className="header-left">
        <form className="search-form" onSubmit={handleSearch}>
          {(() => {
            const colors = selectedCompany?.brand_colors;
            const hasColors = Array.isArray(colors) && colors.length > 0;
            const gradientColors = hasColors ? [...colors, colors[0]].join(', ') : '';
            return hasColors ? (
              <div style={{ position: 'relative', flex: 1 }}>
                <style>{`
                  @property --search-angle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
                  @keyframes searchColorChase { to { --search-angle: -360deg; } }
                  .search-gradient-border {
                    background: conic-gradient(from var(--search-angle), ${gradientColors}) !important;
                    animation: searchColorChase 4s linear infinite !important;
                  }
                `}</style>
                {/* Gradient border pill */}
                <div className="search-gradient-border" style={{
                  position: 'relative', borderRadius: 26, padding: 3, zIndex: 1,
                }}>
                  {/* Inner search area — solid opaque bg blocks glow from entering */}
                  <div className="search-input-wrapper" style={{
                    position: 'relative', zIndex: 2,
                    borderRadius: 23,
                    border: 'none',
                  }}>
                    <Search size={18} className="search-icon" />
                    <input
                      type="text"
                      placeholder={searchPlaceholder}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input search-input-solid"
                    />
                    <button type="button" className="search-batch-btn"
                            onClick={() => setBatchOpen(true)}
                            title="Batch search — scan or paste many at once">
                      <ScanLine size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="search-input-wrapper">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <button type="button" className="search-batch-btn"
                        onClick={() => setBatchOpen(true)}
                        title="Batch search — scan or paste many at once">
                  <ScanLine size={16} />
                </button>
              </div>
            );
          })()}
        </form>
        <BatchSearchModal
          open={batchOpen}
          initial={searchTerm}
          onClose={() => setBatchOpen(false)}
          onApply={(joined) => {
            setSearchTerm(joined);
            // The same two things pressing Enter does: tell a live-filtering
            // page immediately, and navigate a page that filters server-side.
            if (liveFilterPage) {
              window.dispatchEvent(new CustomEvent('global-search',
                { detail: { q: joined, path: currentPath } }));
            } else if (!currentPath.startsWith('/settings')) {
              navigate(searchTargetPath);
            }
          }} />
      </div>

      <div className="header-right">
        <div className="header-time">
          <Clock size={16} />
          <div className="time-display">
            <div className="time">{formatTime(now)}</div>
            <div className="date">{formatDate(now)}</div>
          </div>
        </div>

        <div className="header-actions">
          {/* The laboratory's Google Workspace assistant. Never gated at all
              before, so an external client saw a button into the lab's own
              workspace tooling. */}
          {!isExternalUser && (
            <button
              className="icon-btn"
              onClick={() => navigate('/google-workspace')}
              title="Gemini AI"
              style={{ position: 'relative' }}
            >
              <Sparkles size={20} style={{ color: '#4285F4' }} />
            </button>
          )}

          {/* Laboratory tools: logging a call, the notification feed and the
              signed-in user's own mailbox. Anyone here who is not laboratory
              staff has no use for them — see the note on `isExternalUser`
              above for what these actually return to a client, and why that
              makes this a presentation decision rather than a boundary. */}
          {!isExternalUser && (
            <button
              className="icon-btn"
              title="Log a Call"
              onClick={() => setShowCallWizard(true)}
              style={{ position: 'relative' }}
            >
              <Phone size={20} style={{ color: '#10b981' }} />
            </button>
          )}

          {!isExternalUser && (
          <div className="notification-wrapper" ref={notificationRef}>
            <button
              className="icon-btn notification-btn"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </button>

            {showNotifications && (
              <div className="notification-dropdown">
                <div className="dropdown-header">
                  <h3>Notifications</h3>
                  <div className="notif-header-actions">
                    {unreadCount > 0 && (
                      <button className="mark-all-read-btn" onClick={handleMarkAllRead}>
                        Mark All Read
                      </button>
                    )}
                    <span className="notification-count">{unreadCount} new</span>
                  </div>
                </div>
                <div className="notification-list">
                  {notifications.length === 0 ? (
                    <div className="notification-empty">No notifications</div>
                  ) : (
                    notifications.map(notif => {
                      const notifType = (notif.notification_type || 'system').toLowerCase();
                      const IconComponent = NOTIF_ICONS[notifType] || NOTIF_ICONS.system;
                      return (
                        <div
                          key={notif.id}
                          className={`notification-item ${notif.is_read ? 'read' : 'unread'} ${notifType}`}
                          onClick={() => !notif.is_read && handleMarkRead(notif.id)}
                        >
                          <div className="notif-icon-wrap">
                            <IconComponent size={18} />
                          </div>
                          <div className="notification-content">
                            <div className="notification-title">{notif.title}</div>
                            {notif.message && (
                              <div className="notification-message">{notif.message}</div>
                            )}
                            <div className="notification-time">{timeAgo(notif.created_at)}</div>
                          </div>
                          {!notif.is_read && <div className="notif-unread-dot" />}
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="dropdown-footer">
                  <button
                    className="view-all-btn"
                    onClick={() => {
                      navigate('/notifications');
                      setShowNotifications(false);
                    }}
                  >
                    View All
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          {!isExternalUser && (
          <div className="notification-wrapper" ref={gmailRef}>
            <button
              className="icon-btn"
              onClick={async () => {
                setShowGmail(!showGmail);
                if (!showGmail && gmailEmails.length === 0) {
                  setGmailLoading(true); setGmailError('');
                  try {
                    // Pick the inbox backend based on the tenant's
                    // email_provider setting (Corporate Billing → Edit
                    // Company). 'gmail_api' uses DWD impersonation; 'imap'
                    // uses the user's per-account creds from Settings → My
                    // Email.
                    const url = emailProvider === 'gmail_api'
                      ? '/communications/gmail/inbox?max_results=10'
                      : '/communications/inbox?folder=INBOX&limit=10';
                    const r = await api.get(url);
                    if (r.data.error) { setGmailError(r.data.error); }
                    else { setGmailEmails(r.data.emails || []); }
                  } catch { setGmailError('Failed to load inbox'); }
                  setGmailLoading(false);
                }
              }}
            >
              <Mail size={20} />
            </button>

            {showGmail && (
              <div className="notification-dropdown" style={{ width: 380, right: 0 }}>
                <div className="dropdown-header">
                  <h3>Email Inbox</h3>
                  <div className="notif-header-actions">
                    <button className="mark-all-read-btn" onClick={async () => {
                      setGmailLoading(true); setGmailError('');
                      try {
                        const url = emailProvider === 'gmail_api'
                          ? '/communications/gmail/inbox?max_results=10'
                          : '/communications/inbox?folder=INBOX&limit=10';
                        const r = await api.get(url);
                        if (r.data.error) setGmailError(r.data.error);
                        else setGmailEmails(r.data.emails || []);
                      } catch { setGmailError('Failed to refresh'); }
                      setGmailLoading(false);
                    }}>Refresh</button>
                    <button className="mark-all-read-btn" onClick={() => {
                      setShowGmail(false);
                      // If the tenant is on Gmail API, jump to the in-app inbox
                      // reader. Otherwise IMAP users go to their per-user creds
                      // settings (the inbox page is Gmail-only for now).
                      if (emailProvider === 'gmail_api') navigate('/email');
                      else navigate('/settings?section=my-email');
                    }}>{emailProvider === 'gmail_api' ? 'Open Inbox' : 'Open Email Settings'}</button>
                  </div>
                </div>
                <div className="notification-list" style={{ maxHeight: 400 }}>
                  {gmailLoading && <div className="notification-empty">Loading inbox...</div>}
                  {gmailError && <div className="notification-empty" style={{ color: '#dc2626', fontSize: 12 }}>{gmailError}</div>}
                  {!gmailLoading && !gmailError && gmailEmails.length === 0 && <div className="notification-empty">No emails</div>}
                  {gmailEmails.map(email => (
                    <div key={email.id} className="notification-item"
                      style={{ cursor: 'pointer', fontWeight: email.is_read ? 400 : 700, borderLeft: email.is_read ? 'none' : '3px solid #2563eb' }}
                      onClick={() => {
                        // For Gmail-API tenants, navigate to the in-app reader
                        // with the message id so it opens directly. For IMAP
                        // (no inbox page yet) keep the old behavior of fetching
                        // the body to mark it read on the server.
                        if (emailProvider === 'gmail_api') {
                          setShowGmail(false);
                          navigate(`/email?id=${email.id}`);
                        } else {
                          (async () => { try { await api.get(`/communications/inbox/${email.id}?folder=INBOX`); } catch {} })();
                          setGmailEmails(prev => prev.map(e => e.id === email.id ? { ...e, is_read: true } : e));
                        }
                      }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                          <span style={{ fontSize: 12, fontWeight: email.is_read ? 500 : 700, color: 'var(--text-primary, #111827)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                            {(email.from || '').replace(/<.*>/, '').trim() || 'Unknown'}
                          </span>
                          <span style={{ fontSize: 10, color: 'var(--text-secondary, #94a3b8)', whiteSpace: 'nowrap', marginLeft: 8 }}>
                            {email.date ? formatLabDate(email.date) : ''}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-primary, #374151)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: email.is_read ? 400 : 700 }}>
                          {email.subject || '(no subject)'}
                        </div>
                        {email.snippet && (
                          <div style={{ fontSize: 11, color: 'var(--text-secondary, #cbd5e1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2, opacity: email.is_read ? 0.85 : 1 }}>
                            {email.snippet}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}
        </div>

        {/* The identity switcher lives INSIDE the dropdown, below. A second
            control sitting beside the user menu just competed with it. */}
        <div className="user-menu-wrapper" ref={userMenuRef}>
          <button 
            className="user-menu-trigger"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className="user-avatar" style={selectedCompany?.logo_url ? { background: 'transparent', overflow: 'hidden' } : {}}>
              {selectedCompany?.logo_url ? (
                <img src={selectedCompany.logo_url.startsWith('http') ? selectedCompany.logo_url : `/api/v1/saas/companies/${selectedCompanyId}/logo-image`} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (
                <User size={20} />
              )}
            </div>
            <div className="user-info-header">
              <div className="user-name-header">
                {currentUser?.full_name || currentUser?.username || 'User'}
              </div>
              <div className="user-role-header">
                {currentUser?.role || 'Role'}
              </div>
            </div>
            <ChevronDown size={16} className={`chevron ${showUserMenu ? 'rotated' : ''}`} />
          </button>

          {showUserMenu && (
            <div className="user-dropdown">
              <div className="dropdown-section">
                <div className="user-details-full">
                  <div className="user-avatar-large" style={selectedCompany?.logo_url ? { background: 'transparent', overflow: 'hidden' } : {}}>
                    {selectedCompany?.logo_url ? (
                      <img src={selectedCompany.logo_url.startsWith('http') ? selectedCompany.logo_url : `/api/v1/saas/companies/${selectedCompanyId}/logo-image`} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <User size={32} />
                    )}
                  </div>
                  <div className="user-info-full">
                    <div className="user-name-full">
                      {currentUser?.full_name || currentUser?.username}
                    </div>
                    <div className="user-email">{currentUser?.email || 'user@example.com'}</div>
                    <div style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'transparent', border: '1.5px solid #3b82f6', color: '#3b82f6', textTransform: 'uppercase', lineHeight: 1.4 }}>{currentUser?.role}</div>
                  </div>
                </div>
              </div>

              <div className="dropdown-divider"></div>

              {/* "You are also…" — a laboratory admin who is a patient here,
                  or a sales rep, or an external client contact. Renders
                  nothing for the great majority of people, who are one thing. */}
              <IdentitySwitcher inline onContext={setIdentityContext} />

              {/* `parentCompanies` and `labs` come from administrator endpoints
                  that refuse a client login, so for an external practice both
                  are empty — and this gate then hid the practice and
                  laboratory pickers below, which are built from `myContext`
                  and work perfectly well for exactly that login. A client
                  attached to two practices had no way to choose between them.
                  The gate now also opens on what the session itself reports. */}
              {(parentCompanies.length > 0 || labs.length > 0
                || (myContext?.clients || []).length > 1
                || (myContext?.laboratories || []).length > 0) && (
                <>
                  {/* Company selector — show whenever the user has access to
                      MORE THAN ONE company. The `isCorporateUser` flag (set by
                      LabContext) already enforces the right role gate
                      (superadmin / is_global / multi-tenant admin). Previously
                      hardcoded role === 'superadmin' meant non-superadmin
                      multi-tenant admins (like a user explicitly assigned to
                      Brio + Xpiva + Advanced Biomedical via user_companies)
                      saw the company as plain read-only text with no way to
                      switch — even when isCorporateUser was true. */}
                  {isCorporateUser && parentCompanies.length > 1 && (
                    <div className="dropdown-section lab-selector-section">
                      <div className="lab-selector-header">
                        <Building2 size={16} />
                        <span>Company</span>
                      </div>
                      <button
                        className="lab-selector-button"
                        onClick={() => setShowCompanySelector(!showCompanySelector)}
                      >
                        <span className="selected-lab-name">
                          {selectedCompany?.legal_name || selectedCompany?.dba_name || selectedCompany?.company_name || 'Select Company'}
                        </span>
                        <ChevronDown size={14} className={`chevron ${showCompanySelector ? 'rotated' : ''}`} />
                      </button>
                      {showCompanySelector && (
                        <div className="lab-list-inline">
                          {parentCompanies.map(company => (
                            <button
                              key={company.parent_company_id}
                              className={`lab-item-inline ${selectedCompanyId === company.parent_company_id ? 'active' : ''}`}
                              onClick={() => handleCompanySelect(company)}
                            >
                              <div className="lab-item-content">
                                <div className="lab-item-name">{company.legal_name || company.dba_name || company.company_name || 'Unnamed'}</div>
                              </div>
                              {selectedCompanyId === company.parent_company_id && (
                                <div className="lab-item-check">✓</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Company name (read-only) — shown when the dropdown above
                      is NOT shown: user lacks corporate access OR only has one
                      company assigned. Mirrors the inverse of the dropdown gate. */}
                  {/* Which client practice this login is acting as. Only
                      rendered when it is attached to more than one — with a
                      single practice there is nothing to choose, and with
                      none this is not a client login at all. */}
                  {(myContext?.clients || []).length > 1 && (
                    <div className="dropdown-section lab-selector-section">
                      <div className="lab-selector-header">
                        <Building2 size={16} />
                        <span>Practice</span>
                      </div>
                      <div className="lab-list-inline" style={{ display: 'block' }}>
                        <button
                          className={`lab-item-inline ${!selectedPractice ? 'active' : ''}`}
                          onClick={() => choosePractice('')}>
                          <div className="lab-item-content">
                            <div className="lab-item-name"
                                 style={{ fontStyle: !selectedPractice ? 'normal' : 'italic' }}>
                              All my practices
                            </div>
                          </div>
                          {!selectedPractice && <div className="lab-item-check">✓</div>}
                        </button>
                        {myContext.clients.map(c => (
                          <button key={c.client_id}
                            className={`lab-item-inline ${selectedPractice === c.client_id ? 'active' : ''}`}
                            onClick={() => choosePractice(c.client_id)}>
                            <div className="lab-item-content">
                              <div className="lab-item-name">{c.client_name}</div>
                              <div className="lab-item-clia">{c.client_role}</div>
                            </div>
                            {selectedPractice === c.client_id && <div className="lab-item-check">✓</div>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* A client login cannot call the companies endpoint, so
                      `selectedCompany` stays null and this section used to be
                      skipped entirely — leaving a LABORATORY with no company
                      above it, which reads as though the laboratory IS the
                      company. They are different things: "California" is a
                      CLIA-licensed facility; "9 Labs" is the company that owns
                      it. The identity lookup already knows the company name,
                      so fall back to it rather than showing nothing. */}
                  {(selectedCompany || myContext?.company?.name || identityContext?.laboratory)
                   && !(isCorporateUser && parentCompanies.length > 1) && (
                    <div className="dropdown-section lab-selector-section">
                      <div className="lab-selector-header">
                        <Building2 size={16} />
                        <span>Company</span>
                      </div>
                      <div style={{ padding: '6px 20px', fontSize: 13, color: 'var(--text-primary, #111827)', fontWeight: 500 }}>
                        {selectedCompany?.legal_name || selectedCompany?.dba_name
                          || myContext?.company?.name || identityContext?.laboratory || 'Unnamed'}
                      </div>
                    </div>
                  )}

                  {/* When the administrator lab endpoint refuses this login,
                      `labs` is empty and the row vanished — or worse, held a
                      laboratory from whatever company was last in
                      localStorage. Fall back to the labs the SERVER says this
                      session's company has. */}
                  {labs.length === 0 && (myContext?.laboratories || []).length > 0 && (
                    <div className="dropdown-section lab-selector-section">
                      <div className="lab-selector-header">
                        <FlaskConical size={16} />
                        <span>Laboratory</span>
                      </div>
                      {myContext.laboratories.map(l => (
                        <div key={l.lab_id}
                             style={{ padding: '6px 20px', fontSize: 13,
                                      color: 'var(--text-primary, #111827)', fontWeight: 500 }}>
                          {l.lab_name}
                          {l.clia_number && (
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                              CLIA: {l.clia_number}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Lab selector: clickable list only if multiple labs, otherwise show name */}
                  {labs.length === 1 && (
                    <div className="dropdown-section lab-selector-section">
                      <div className="lab-selector-header">
                        <FlaskConical size={16} />
                        <span>Laboratory</span>
                      </div>
                      <div style={{ padding: '6px 20px', fontSize: 13, color: 'var(--text-primary, #111827)', fontWeight: 500 }}>
                        {labs[0].lab_name}
                        {labs[0].clia_number && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>CLIA: {labs[0].clia_number}</div>}
                      </div>
                    </div>
                  )}

                  {labs.length > 1 && (
                    <div className="dropdown-section lab-selector-section">
                      <div className="lab-selector-header">
                        <FlaskConical size={16} />
                        <span>Laboratory</span>
                        {selectedLabId && (currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                          <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 'auto', textTransform: 'none' }}>
                            click selected to deselect
                          </span>
                        )}
                      </div>
                      <div className="lab-list-inline" style={{ display: 'block' }}>
                        {/* "All Labs" — only available to admin / superadmin.
                            Tech / client / patient roles must stay scoped to a
                            specific lab (each lab has its own CLIA license). */}
                        {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                          <button
                            className={`lab-item-inline ${!selectedLabId ? 'active' : ''}`}
                            onClick={() => { clearLab(); setShowUserMenu(false); }}
                            title={currentUser?.role === 'superadmin'
                              ? 'View data across all tenants and labs (system-wide)'
                              : 'View data across every lab in your tenant'}
                          >
                            <div className="lab-item-content">
                              <div className="lab-item-name" style={{ fontStyle: !selectedLabId ? 'normal' : 'italic', opacity: !selectedLabId ? 1 : 0.7 }}>
                                All Labs {currentUser?.role === 'superadmin' ? '(system-wide)' : '(this tenant)'}
                              </div>
                              <div className="lab-item-clia">No lab filter applied</div>
                            </div>
                            {!selectedLabId && (
                              <div className="lab-item-check">✓</div>
                            )}
                          </button>
                        )}

                        {labs.map(lab => (
                          <button
                            key={lab.lab_id}
                            className={`lab-item-inline ${selectedLabId === lab.lab_id ? 'active' : ''}`}
                            onClick={() => handleLabSelect(lab)}
                            title={selectedLabId === lab.lab_id ? 'Click to deselect (view all labs)' : `Switch to ${lab.lab_name}`}
                          >
                            <div className="lab-item-content">
                              <div className="lab-item-name">{lab.lab_name}</div>
                              {lab.clia_number && (
                                <div className="lab-item-clia">CLIA: {lab.clia_number}</div>
                              )}
                            </div>
                            {selectedLabId === lab.lab_id && (
                              <div className="lab-item-check">✓</div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="dropdown-divider"></div>
                </>
              )}

              <div className="dropdown-section">
                <div className="dropdown-actions-grid">
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      navigate('/my-account');
                      setShowUserMenu(false);
                    }}
                  >
                    <User size={20} />
                    <span>My Account</span>
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      navigate('/settings');
                      setShowUserMenu(false);
                    }}
                  >
                    <Settings size={20} />
                    <span>Settings</span>
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      openHelpSidebar();
                      setShowUserMenu(false);
                    }}
                  >
                    <HelpCircle size={20} />
                    <span>Help</span>
                  </button>
                  <button
                    className="dropdown-item logout"
                    onClick={onLogout}
                  >
                    <LogOut size={20} />
                    <span>Logout</span>
                  </button>
                </div>
                <div
                  style={{ padding: '6px 0 2px 36px', fontSize: '0.75em', color: '#3b82f6', textAlign: 'left', lineHeight: 1, cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); setShowChangelog(true); setShowUserMenu(false); }}
                  title="Click to view changelog"
                >
                  Version: {appVersion} &rsaquo;
                </div>
                <div style={{ padding: '2px 0 4px 36px', fontSize: '0.65em', color: 'var(--text-muted, #94a3b8)', textAlign: 'left', lineHeight: 1, fontFamily: 'monospace' }}>
                  {window.location.host}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Changelog Modal */}
      {showChangelog && (
        <div className="changelog-overlay" >
          <div className="changelog-modal" onClick={e => e.stopPropagation()}>
            <div className="changelog-header">
              <h2>What's New</h2>
              <button className="changelog-close" onClick={() => setShowChangelog(false)}>&times;</button>
            </div>
            <div className="changelog-body">
              {versionHistory.map((release) => (
                <div key={release.version} className="changelog-release">
                  <div className="changelog-version-header">
                    <span className="changelog-version-badge">v{release.version}</span>
                    <span className="changelog-date">{release.date}</span>
                    {release.version === appVersion && <span className="changelog-current-badge">Current</span>}
                  </div>
                  {(release.sections || []).map((section, si) => (
                    <div key={`${section.title || 'section'}-${si}`} className="changelog-section">
                      <h4 className="changelog-section-title">{section.title}</h4>
                      <ul className="changelog-items">
                        {(section.items || []).map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Call Log wizard — opened from the green phone icon in the header.
          Uses position:fixed internally so it can mount inside <header>. */}
      {showCallWizard && (
        <CallLogWizard
          onClose={() => setShowCallWizard(false)}
          onSaved={() => setShowCallWizard(false)} />
      )}
    </header>
  );
};

export default Header;
